import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { CheckCircle, Clock, MapPin, X } from 'lucide-react';
import { publicApi } from '../services/api';
import { companyOf, DEFAULT_COMPANY } from '../utils/company';
import { absUrl } from '../utils/commercial';
import { PublicPage, PublicHeader, PublicFooter, PublicLoading, Panel, H1, H2 } from '../components/public';

/**
 * The service report a commercial site's manager opens from the email after a
 * visit: where, when, what was done, and the crew's photos. No prices, no crew
 * notes — the server leaves those out.
 */
export default function ServiceReportPublic() {
  const { token } = useParams<{ token: string }>();
  const [zoom, setZoom] = useState<string | null>(null);
  const { data, isLoading, isError } = useQuery(
    ['public-service-report', token],
    () => publicApi.get(`/commercial/public/report/${token}`).then(r => r.data),
    { enabled: !!token, retry: false },
  );
  const company = companyOf(data);

  if (isLoading) return <PublicLoading />;
  if (isError || !data?.report) return <NotFound />;

  const r = data.report;
  const services: string[] = Array.isArray(r.services) ? r.services : [];
  const photos: string[] = Array.isArray(r.photos) ? r.photos : [];
  const place = r.site && r.site !== r.address ? r.site : null;

  return (
    <PublicPage company={company} context={r.account || 'Service report'}>
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift p-5">
        <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">Service report</p>
        <H1 className="mt-1">{r.account || company.name}</H1>
        <div className="mt-3 flex items-start gap-2 text-[15px] text-gray-800">
          <MapPin className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden />
          <span className="break-words">
            {place && <span className="block font-bold text-gray-900">{place}</span>}
            {r.address}
          </span>
        </div>
        {r.date_label && <p className="mt-2 text-[15px] font-semibold text-gray-900">{r.date_label}</p>}
        {r.minutes != null && Number(r.minutes) > 0 && (
          <p className="mt-1 flex items-center gap-1.5 text-[15px] text-gray-800">
            <Clock className="h-4 w-4 text-gray-600" aria-hidden /> On site {Math.round(Number(r.minutes))} min
          </p>
        )}
      </section>

      {services.length > 0 && (
        <Panel title="Work completed">
          <ul className="space-y-2">
            {services.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-[15px] text-gray-900">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden />
                <span className="break-words">{s}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {r.note && (
        <Panel title="Note from us" tone="brand">
          <p className="text-[15px] text-gray-900 whitespace-pre-line break-words">{r.note}</p>
        </Panel>
      )}

      {photos.length > 0 && (
        <Panel title={`Photos (${photos.length})`}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <button key={i} type="button" onClick={() => setZoom(absUrl(p))} className="press block aspect-square overflow-hidden rounded-2xl bg-gray-100">
                <img src={absUrl(p)} alt={`The site after the visit, ${i + 1} of ${photos.length}`} loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <p className="mt-2 text-[13px] text-gray-600">Tap a photo to see it full size.</p>
        </Panel>
      )}

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-3" onClick={() => setZoom(null)}>
          <button type="button" aria-label="Close photo" onClick={() => setZoom(null)}
            className="absolute top-3 right-3 h-11 w-11 rounded-2xl bg-white/15 text-white flex items-center justify-center">
            <X className="h-6 w-6" />
          </button>
          <img src={zoom} alt="The site after the visit, full size" className="max-h-full max-w-full object-contain rounded-xl" />
        </div>
      )}
    </PublicPage>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <PublicHeader company={DEFAULT_COMPANY} />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="bg-white rounded-3xl border border-brand-100 shadow-lift p-6 max-w-sm w-full text-center">
          <H2>Report not found</H2>
          <p className="text-[15px] text-gray-800 mt-3">
            This link may be mistyped or too old. Text or call us and we will send it again.
          </p>
        </div>
      </div>
      <PublicFooter company={DEFAULT_COMPANY} />
    </div>
  );
}
